"use client";

import { useStytchMember } from "@stytch/nextjs/b2b";
import React, { createContext, useContext, useState, ReactNode } from "react";

import { useGroup } from "@/src/lib/data-fetchers/group-fetchers";
import { Group } from "@/src/models/group";

interface UserContextProps {
  email: string | null;
  stytchOrgId: string | null;
  group: Group | null;
  tenancy: string | null;
  roles: string[];
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { member } = useStytchMember();
  const { data: group } = useGroup(member?.organization_id);
  const [userState, setUserState] = useState<UserContextProps>({
    email: null,
    stytchOrgId: null,
    group: null,
    tenancy: null,
    roles: [],
  });

  React.useEffect(() => {
    if (member && group) {
      // Extract role IDs from Stytch member roles
      const roles: string[] = member.roles?.map((r) => r.role_id) || [];

      setUserState({
        email: member.email_address || "",
        stytchOrgId: member.organization_id,
        group: group,
        tenancy: group.tenancy,
        roles,
      });
    }
  }, [member, group]);

  return <UserContext.Provider value={userState}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};
